'use client';
import { useEffect, useRef, useState } from 'react';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { BasePrimaryButton, useToast } from '@practium/ui';
import styles from './ProfileHeader.module.css';
import { useNavigate } from 'react-router-dom';
import { Person } from '@mui/icons-material';

interface ProfileHeaderProps {
	name: string;
	email: string;
	avatar?: string;
	backgroundUrl?: string;
	isEditMode?: boolean;
	onModify?: () => void;
	onSave?: () => void;
	onAvatarChange?: (file: File, preview: string) => void;
	onBackgroundChange?: (file: File, preview: string) => void;
	isLoading?: boolean;
}

export const ProfileHeader = ({
	name,
	email,
	avatar,
	backgroundUrl,
	isEditMode = false,
	onModify,
	onSave,
	onAvatarChange,
	onBackgroundChange,
	isLoading,
}: ProfileHeaderProps) => {
	const avatarInputRef = useRef<HTMLInputElement>(null);
	const backgroundInputRef = useRef<HTMLInputElement>(null);
	const navigate = useNavigate();
	const toast = useToast();
	const [avatarError, setAvatarError] = useState(false);
	const [backgroundError, setBackgroundError] = useState(false);

	useEffect(() => {
		setAvatarError(false);
	}, [avatar]);

	useEffect(() => {
		setBackgroundError(false);
		if (!backgroundUrl) return;

		const image = new Image();
		image.onerror = () => setBackgroundError(true);
		image.src = backgroundUrl;
	}, [backgroundUrl]);

	const handleFileChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		onChange?: (file: File, preview: string) => void
	) => {
		if (!onChange) return;

		const file = e.target.files?.[0];
		if (!file) return;

		if (file.size > 2e6) {
			toast.error('O arquivo deve ter no máximo 2MB');
			e.target.value = '';
			return;
		}

		if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
			toast.error('Formato de imagem não suportado. Use JPG, PNG ou WEBP');
			e.target.value = '';
			return;
		}

		const reader = new FileReader();
		reader.onloadend = () => {
			const result = reader.result as string;
			onChange(file, result);
		};
		reader.readAsDataURL(file);
	};

	return (
		<div className={styles.profileHeader}>
			<div
				className={`${styles.backgroundPanel} ${!backgroundUrl || backgroundError ? styles.backgroundFallback : ''}`}
				style={
					backgroundUrl && !backgroundError
						? { backgroundImage: `url("${backgroundUrl}")` }
						: undefined
				}
			>
				{isEditMode && (
					<div className={`${styles.backgroundOverlay} ${isEditMode ? styles.alwaysVisible : ''}`}>
						<button
							className={styles.uploadButton}
							onClick={() => backgroundInputRef.current?.click()}
							disabled={isLoading}
							type="button"
							aria-label="Alterar imagem de fundo"
						>
							<FileUploadOutlinedIcon className={styles.uploadIcon} />
						</button>
					</div>
				)}
				{isEditMode && (
					<input
						ref={backgroundInputRef}
						type="file"
						accept="image/jpeg,image/jpg,image/png,image/webp"
						onChange={(e) => handleFileChange(e, onBackgroundChange)}
						className={styles.fileInput}
						disabled={isLoading}
					/>
				)}
			</div>

			<div className={styles.profileContent}>
				<div className={styles.avatarContainer}>
					{avatar && !avatarError ? (
						<img
							src={avatar}
							alt={name}
							className={styles.avatar}
							onError={() => setAvatarError(true)}
						/>
					) : (
						<div className={styles.avatarFallback}>
							<Person className={styles.avatarFallbackIcon} />
						</div>
					)}
					{isEditMode && (
						<>
							<div className={`${styles.avatarOverlay} ${isEditMode ? styles.alwaysVisible : ''}`}>
								<button
									className={styles.uploadButton}
									onClick={() => avatarInputRef.current?.click()}
									disabled={isLoading}
									type="button"
									aria-label="Alterar foto de perfil"
								>
									<FileUploadOutlinedIcon className={styles.uploadIcon} />
								</button>
							</div>
							<input
								ref={avatarInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png,image/webp"
								onChange={(e) => handleFileChange(e, onAvatarChange)}
								className={styles.fileInput}
								disabled={isLoading}
							/>
						</>
					)}
				</div>
				<div className={styles.userInfo}>
					<h1 className={styles.userName}>{name}</h1>
					<p className={styles.userEmail}>{email}</p>
				</div>
			</div>

			<div className={styles.actionContainer}>
				{isEditMode ? (
					<div className={styles.buttonWrapper}>
						<button onClick={() => navigate(-1)} className={styles.backButton}>
							Voltar
						</button>
						<BasePrimaryButton onClick={onSave} className={styles.saveButton} disabled={isLoading}>
							Salvar
						</BasePrimaryButton>
					</div>
				) : (
					<BasePrimaryButton onClick={onModify} className={styles.modifyButton}>
						Modificar
					</BasePrimaryButton>
				)}
			</div>
		</div>
	);
};
